import { CheckCircle, Smartphone, Bookmark, Monitor } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";

// iOS-style share icon component
const IOSShareIcon = ({ className }: { className?: string }) => (
  <img
    src="/apple_share_icon.svg"
    alt="iOS share icon"
    className={className}
  />
);

const Step = ({ number, title, children }: { number: number; title: string; children: React.ReactNode }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
      <span className="text-primary font-semibold">{number}</span>
    </div>
    <div className="flex-1">
      <p className="font-medium mb-1">{title}</p>
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  </div>
);

const InstallApp = () => {
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      return isStandalone || isIOSStandalone;
    };

    setIsInstalled(checkInstalled());
  }, []);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Install the ETP App
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Add the ETP Executive Network site as an app on your phone or bookmark it in your browser
          </p>
        </div>

        {isInstalled && (
          <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-100">
                    App Already Installed
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    You're currently using the installed version of the app.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {/* iPhone Install */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <IOSShareIcon className="w-6 h-6" />
                <CardTitle>Install on iPhone</CardTitle>
              </div>
              <CardDescription>
                Add the ETP app to your iPhone home screen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Step number={1} title="Open this website in Safari">
                  Make sure you're viewing members.alexepstein.ai in the Safari browser.
                </Step>
                <Step number={2} title='Tap the "..." menu, then Share'>
                  Tap the three-dot menu (...) at the bottom right of Safari, then tap Share.
                </Step>
                <Step number={3} title='Select "Add to Home Screen"'>
                  Scroll through the share menu and tap "Add to Home Screen". It might be under "More."
                </Step>
                <Step number={4} title='Tap "Add"'>
                  Tap "Add" in the top right corner. The app icon will appear on your home screen.
                </Step>
              </div>
            </CardContent>
          </Card>

          {/* Android Install */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Smartphone className="w-6 h-6" />
                <CardTitle>Install on Android</CardTitle>
              </div>
              <CardDescription>
                Add the app to your Android home screen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Step number={1} title="Open this website in Chrome">
                  Make sure you're viewing members.alexepstein.ai in the Google Chrome browser.
                </Step>
                <Step number={2} title='Tap the "..."menu button'>
                  Tap the three-dot menu (...) in the top right corner of Chrome.
                </Step>
                <Step number={3} title='Tap "Add to Home screen" or "Install app"'>
                  You may see either option depending on your device. Both do the same thing.
                </Step>
                <Step number={4} title='Tap "Install" or "Add"'>
                  Tap "Install" or "Add," whichever comes up. The app icon will appear on your home screen.
                </Step>
              </div>
            </CardContent>
          </Card>

          {/* Bookmark Alternative */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Bookmark className="w-6 h-6" />
                <CardTitle>Bookmark the Website</CardTitle>
              </div>
              <CardDescription>
                Bookmark the website for easy access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <p className="font-medium mb-2">On iPhone (Safari)</p>
                  <div className="space-y-3 ml-0">
                    <Step number={1} title="Open this website in Safari">
                      Navigate to members.alexepstein.ai in Safari.
                    </Step>
                    <Step number={2} title='Tap the "..." menu'>
                      Tap the three-dot menu (...) at the bottom right of Safari.
                    </Step>
                    <Step number={3} title='Tap "Add to Bookmarks"'>
                      Choose "Add to Bookmarks" from the menu, then tap Save.
                    </Step>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium mb-2">On Android (Chrome)</p>
                  <div className="space-y-3 ml-0">
                    <Step number={1} title="Open this website in Chrome">
                      Navigate to members.alexepstein.ai in Google Chrome.
                    </Step>
                    <Step number={2} title="Tap the menu button">
                      Tap the three-dot menu (...) in the top right corner.
                    </Step>
                    <Step number={3} title='Tap the star icon or "Bookmark"'>
                      This saves the page to your bookmarks.
                    </Step>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium mb-2">On Desktop</p>
                  <div className="space-y-3 ml-0">
                    <Step number={1} title="Open this website in your browser">
                      Go to members.alexepstein.ai.
                    </Step>
                    <Step number={2} title="Bookmark the page">
                      Press Ctrl+D (Windows) or Cmd+D (Mac), or click the star/bookmark icon in the address bar.
                    </Step>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InstallApp;

